<template>
	<div class="auth-wrapper auth-v2">
		<b-row class="auth-inner m-0">
			<!-- Brand logo-->
			<b-link class="brand-logo">
				<vuexy-logo />

				<h2 class="brand-text text-primary ml-1">Vuexy</h2>
			</b-link>
			<!-- /Brand logo-->

			<!-- Left Text-->
			<b-col lg="8" class="d-none d-lg-flex align-items-center p-5">
				<div
					class="w-100 d-lg-flex align-items-center justify-content-center px-5"
				>
					<b-img fluid :src="imgUrl" alt="Forgot password V2" />
				</div>
			</b-col>
			<!-- /Left Text-->

			<!-- Forgot password-->
			<b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
				<b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
					<!-- Step 1: identify the account by email/username -->
					<template v-if="step === 'request'">
						<b-card-title class="mb-1"> Forgot Password? 🔒 </b-card-title>
						<b-card-text class="mb-2">
							Enter your email or username and, if it matches an account, we'll send an OTP to the phone number on file.
						</b-card-text>

						<validation-observer ref="requestRules">
							<b-form
								class="auth-forgot-password-form mt-2"
								@submit.prevent="sendOtp"
							>
								<b-form-group label="Email or Username" label-for="forgot-password-identifier">
									<validation-provider
										#default="{ errors }"
										name="Email or Username"
										rules="required"
									>
										<b-form-input
											id="forgot-password-identifier"
											v-model="identifier"
											:state="errors.length > 0 ? false : null"
											name="forgot-password-identifier"
											placeholder="Email or username"
											autofocus
										/>
										<small class="text-danger">{{ errors[0] }}</small>
									</validation-provider>
								</b-form-group>

								<b-button type="submit" variant="primary" block :disabled="sending">
									{{ sending ? "Sending..." : "Send OTP" }}
								</b-button>
							</b-form>
						</validation-observer>
					</template>

					<!-- Step 2: enter OTP + new password. The phone number is never
					     shown or collected here — the backend already resolved it
					     from the identifier in step 1 and correlates everything by
					     an opaque session_token instead (see OTPController). -->
					<template v-else>
						<b-card-title class="mb-1"> Verify OTP 🔑 </b-card-title>
						<b-card-text class="mb-2">
							If that account exists, an OTP was sent to its registered phone number. Enter it below along with your new password.
						</b-card-text>

						<validation-observer ref="resetRules">
							<b-form
								class="auth-forgot-password-form mt-2"
								@submit.prevent="resetPassword"
							>
								<b-form-group label="OTP" label-for="forgot-password-otp">
									<validation-provider
										#default="{ errors }"
										name="OTP"
										rules="required|digits:6"
									>
										<b-form-input
											id="forgot-password-otp"
											v-model="otp"
											:state="errors.length > 0 ? false : null"
											name="forgot-password-otp"
											placeholder="6-digit code"
											maxlength="6"
											autofocus
										/>
										<small class="text-danger">{{ errors[0] }}</small>
									</validation-provider>
								</b-form-group>

								<b-form-group label="New Password" label-for="forgot-password-new">
									<validation-provider
										#default="{ errors }"
										name="Password"
										vid="password"
										rules="required|min:8"
									>
										<b-form-input
											id="forgot-password-new"
											v-model="password"
											type="password"
											:state="errors.length > 0 ? false : null"
											name="forgot-password-new"
											placeholder="At least 8 characters"
										/>
										<small class="text-danger">{{ errors[0] }}</small>
									</validation-provider>
								</b-form-group>

								<b-form-group label="Confirm New Password" label-for="forgot-password-confirm">
									<validation-provider
										#default="{ errors }"
										name="Confirm Password"
										rules="required|confirmed:password"
									>
										<b-form-input
											id="forgot-password-confirm"
											v-model="confirmPassword"
											type="password"
											:state="errors.length > 0 ? false : null"
											name="forgot-password-confirm"
											placeholder="Re-enter new password"
										/>
										<small class="text-danger">{{ errors[0] }}</small>
									</validation-provider>
								</b-form-group>

								<b-button type="submit" variant="primary" block :disabled="submitting">
									{{ submitting ? "Resetting..." : "Reset Password" }}
								</b-button>

								<b-button variant="flat-secondary" block class="mt-1" :disabled="sending" @click="sendOtp">
									{{ sending ? "Resending..." : "Resend OTP" }}
								</b-button>
							</b-form>
						</validation-observer>
					</template>

					<p class="text-center mt-2">
						<b-link :to="{ name: 'auth-login' }">
							<feather-icon icon="ChevronLeftIcon" /> Back to login
						</b-link>
					</p>
				</b-col>
			</b-col>
			<!-- /Forgot password-->
		</b-row>
	</div>
</template>

<script>
/* eslint-disable global-require */
import VuexyLogo from "@core/layouts/components/Logo.vue";
import {
	BRow,
	BCol,
	BLink,
	BCardTitle,
	BCardText,
	BImg,
	BForm,
	BFormGroup,
	BFormInput,
	BButton,
} from "bootstrap-vue";
import { required, confirmed, digits, min } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store/index";
import axiosIns from "@/libs/axios";

export default {
	components: {
		VuexyLogo,
		BRow,
		BCol,
		BLink,
		BImg,
		BForm,
		BButton,
		BFormGroup,
		BFormInput,
		BCardTitle,
		BCardText,
	},
	data() {
		return {
			// "request" (enter identifier, ask for OTP) -> "reset" (enter OTP + new password)
			step: "request",
			identifier: "",
			sessionToken: "",
			otp: "",
			password: "",
			confirmPassword: "",
			sending: false,
			submitting: false,
			sideImg: require("@/assets/images/pages/forgot-password-v2.svg"),
			// validation
			required,
			confirmed,
			digits,
			min,
		};
	},
	computed: {
		imgUrl() {
			if (store.state.appConfig.layout.skin === "dark") {
				// eslint-disable-next-line vue/no-side-effects-in-computed-properties
				this.sideImg = require("@/assets/images/pages/forgot-password-v2-dark.svg");
				return this.sideImg;
			}
			return this.sideImg;
		},
	},
	methods: {
		showToast(title, variant = "success", icon = "CheckIcon") {
			this.$toast({
				component: ToastificationContent,
				props: { title, icon, variant },
			});
		},
		errorMessage(error, fallback) {
			const data = error.response?.data;
			if (!data) return fallback;
			if (typeof data === "string") return data;
			if (data.message) {
				return typeof data.message === "string" ? data.message : fallback;
			}
			if (data.error) {
				if (typeof data.error === "string") return data.error;
				const firstField = Object.values(data.error)[0];
				return Array.isArray(firstField) ? firstField[0] : fallback;
			}
			return fallback;
		},
		sendOtp() {
			this.$refs.requestRules.validate().then((success) => {
				if (!success) return;

				this.sending = true;
				axiosIns
					.post("send-otp", { identifier: this.identifier })
					.then((res) => {
						// Deliberately the same message/flow whether or not the
						// identifier matched a real account (see OTPController::sendOtp) —
						// the UI must not reveal account existence either.
						this.sessionToken = res.data?.session_token || "";
						this.showToast(
							res.data?.message || "If an account matches, an OTP has been sent to the registered phone number.",
						);
						this.step = "reset";
					})
					.catch((error) => {
						this.showToast(
							this.errorMessage(error, "Failed to send OTP. Please try again."),
							"danger",
							"AlertTriangleIcon",
						);
					})
					.finally(() => {
						this.sending = false;
					});
			});
		},
		resetPassword() {
			this.$refs.resetRules.validate().then((success) => {
				if (!success) return;

				this.submitting = true;
				axiosIns
					.post("verify-otp", { session_token: this.sessionToken, otp: this.otp })
					.then(() =>
						axiosIns.post("reset-password", {
							session_token: this.sessionToken,
							otp: this.otp,
							password: this.password,
							confirm_password: this.confirmPassword,
						}),
					)
					.then(() => {
						this.showToast("Password reset successfully. Please login with your new password.");
						this.$router.push({ name: "auth-login" }).catch(() => {});
					})
					.catch((error) => {
						this.showToast(
							this.errorMessage(error, "Failed to reset password. Please try again."),
							"danger",
							"AlertTriangleIcon",
						);
					})
					.finally(() => {
						this.submitting = false;
					});
			});
		},
	},
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
